import { MatchStatus, MenuLocation, PatientType, RecipientAction } from '@nkr/enums/index';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { useContext } from 'react';
import { RecipientProfileContext } from './models';
import ReactHtmlParser from 'react-html-parser';
import OverlayMenuButton from '@nkr/components/overlay-menu-button';

const menuItemList = [
    { action: RecipientAction.Edit, title: 'Edit' },
    { action: RecipientAction.PairedDonors, title: 'Paired Donors' },
    { action: RecipientAction.Toolbox, title: 'Toolbox' },
    { action: RecipientAction.PotentialDonors, title: 'Pre-Selects' },
    { action: RecipientAction.VoucherDonor, title: 'Voucher Donor' },
    { action: RecipientAction.ViewMicrosite, title: 'View Microsite Registration' },
    { action: RecipientAction.Order3gTissueTyping, title: 'Order High Res. Tissue Typing (Cheek Swab)' },
    { action: RecipientAction.ReprintRequisition, title: 'Reprint Recipient Requisition' },
    { action: RecipientAction.UtilizeBulkCheekSwab, title: 'Utilize a Bulk Cheek Swab (Kit on hand)' },
    { action: RecipientAction.EnrollRecipientShield, title: 'Enroll Recipient Shield' },
    { action: RecipientAction.WaitListReplace, title: 'Waitlist Replace' },
    { action: RecipientAction.Delete, title: 'Delete' },
    { action: -1, title: '' }
];

interface IProps {
    recipientId: number,
    centerId: number,
    hasMedicareAb: boolean,
    centerHasRecipientShield: boolean,
    recipientShieldEnrolled: boolean,
    statusId: number,
    orderId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    menuLocation: MenuLocation,
    recipientCenterId: number,
    patientTypeId: number,
    voucherDonorId: number,
    voucherDonorAlias: string,
    voucherDonorCenterId: number,
    canOrderCheekSwab: boolean,
    canDelete: boolean,
    isWaitlistEligible: boolean,
    micrositeRecipientId: number,
    onMenuToggle: (show: boolean) => void,
    onMenuDisplayed?: (id: number) => void,
    onItemSelected?: (id: number, action: RecipientAction) => void
}

const ActionButtonRenderer = (props: IProps) => {
    const context = useContext(RecipientProfileContext);

    const handleMenuToggle = (show: boolean) => {
        props.onMenuToggle(show);
    };

    const handleMenuSelection = (id: any, action: number) => {
        if (action == RecipientAction.Order3gTissueTyping) {
            context.onItemSelected(id, RecipientAction.Order3gTissueTyping);
        }

        if (action == RecipientAction.UtilizeBulkCheekSwab) {
            context.onItemSelected(id, RecipientAction.UtilizeBulkCheekSwab);
        }

        if (action == RecipientAction.Delete) {
            context.onItemSelected(id, RecipientAction.Delete);
        }

        if (action == RecipientAction.ReprintRequisition) {
            context.onItemSelected(props.orderId, RecipientAction.ReprintRequisition);
        }
    };

    const handleSideBarMenuSelection = (e: any) => {
        let action = parseInt(e);
        let url = getUrl(action, props.recipientId);

        if (url != null) {
            window.location.href = url;
        } else if (action == RecipientAction.Order3gTissueTyping || action == RecipientAction.UtilizeBulkCheekSwab || action == RecipientAction.Delete || action == RecipientAction.ReprintRequisition) {
            context.onItemSelected(props.orderId, action);
        }
    };

    const getUrl = (action: RecipientAction, id: number) => {
        let hrefPrefix = !props.isAdmin ? `/center/${props.centerId}` : `/center/${props.recipientCenterId}`;
        let recpTab = props.statusId == MatchStatus.Transplanted ? `post-transplant` : `general`;

        switch (action) {
            case RecipientAction.Edit: return `${hrefPrefix}/recipient/${id}/profile/${recpTab}`;
            case RecipientAction.PairedDonors: return `${hrefPrefix}/recipient/${id}/profile/paired-donors`;
            case RecipientAction.Toolbox: return `${hrefPrefix}/recipient/${id}/toolbox`;
            case RecipientAction.PotentialDonors: return `${hrefPrefix}/recipient/${id}/potential-donors`;
            case RecipientAction.WaitListReplace: return `${hrefPrefix}/recipient/${id}/waitlist-replace`;
            case RecipientAction.VoucherDonor: return `/center/${props.voucherDonorCenterId}/donor/${props.voucherDonorId}/profile`;
            case RecipientAction.ViewMicrosite: return `/center/${props.centerId}/microsite/${props.micrositeRecipientId}/registration`;
        }

        return null;
    };

    const getMenuItems = () => {
        const data = props;

        let menuItems = [];

        if (props.menuLocation != MenuLocation.Profile) {
            menuItems.push(RecipientAction.Edit);
        }

        if (props.patientTypeId == PatientType.Paired) {
            menuItems.push(RecipientAction.PairedDonors);
        }

        menuItems.push(RecipientAction.Toolbox);
        menuItems.push(RecipientAction.PotentialDonors);

        if (props.voucherDonorId > 0 && (props.recipientCenterId == props.voucherDonorCenterId || props.isAdminLogin)) {
            menuItems.push(RecipientAction.Divider);
            menuItems.push(RecipientAction.VoucherDonor);
        }
        
        if (props.micrositeRecipientId > 0) {
            menuItems.push(RecipientAction.Divider);
            menuItems.push(RecipientAction.ViewMicrosite);
        }

        menuItems.push(RecipientAction.Divider);

        menuItems.push(RecipientAction.Order3gTissueTyping);

        if (data.orderId > 0) {
            menuItems.push(RecipientAction.ReprintRequisition);
        } else {
            menuItems.push(RecipientAction.UtilizeBulkCheekSwab);
        }

        if (data.hasMedicareAb && data.centerHasRecipientShield) {
            if (data.recipientShieldEnrolled) {
                menuItems.push(RecipientAction.EnrollRecipientShieldAlready);
            } else {
                menuItems.push(RecipientAction.EnrollRecipientShield);
            }
        }

        if (props.isWaitlistEligible) {
            menuItems.push(RecipientAction.Divider);
            menuItems.push(RecipientAction.WaitListReplace);
        }

        if (data.canDelete) {
            menuItems.push(RecipientAction.Divider);
            menuItems.push(RecipientAction.Delete)
        }

        return menuItems;
    }

    if (props.recipientId == null)
        return null;

    const menuItems = getMenuItems().map(item => {
        return menuItemList.find(x => x.action == item);
    });

    if (props.menuLocation == MenuLocation.Profile || props.menuLocation == MenuLocation.Sidebar) {
        return (
            <Dropdown id={`action-menu-${props.recipientCenterId}`} onSelect={handleSideBarMenuSelection} style={{ width: '100%' }} pullRight={props.menuLocation == MenuLocation.Profile}>
                <Dropdown.Toggle bsSize={props.menuLocation == MenuLocation.Profile ? null : 'sm'} style={{ width: '100%' }}>
                    Recipient Actions
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {menuItems.map((item, index) => {
                        let url = item.action != -1 ? getUrl(item.action, props.recipientId) : null;
                        let id = item.action >= 0 ? props.recipientId : 'div-' + props.recipientId + '-' + Math.random() * 10000;
                        let title = item.title;
            
                        if (item.action == RecipientAction.VoucherDonor) {
                            title = 'Voucher Donor (' + props.voucherDonorAlias + ')';
                        }

                        if (item.action == RecipientAction.Divider) {
                            return (
                                <MenuItem key={'divider-' + index} divider />
                            );
                        }
                        return (
                            <MenuItem key={'action-' + index} href={url} eventKey={item.action}>{title}</MenuItem>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        );
    } else {
        const menu = menuItems.map(item => {
            let url = item.action != -1 ? getUrl(item.action, props.recipientId) : null;
            let id = item.action >= 0 ? props.recipientId : 'div-' + props.recipientId + '-' + Math.random() * 10000;
            let title = item.title;

            if (item.action == RecipientAction.VoucherDonor) {
                title = 'Voucher Donor (' + props.voucherDonorAlias + ')';
            }

            if (url == null) {
                if (item.action >= 0) {
                    return { id, action: item.action, include: true, selectable: true, url, element: <div>{ReactHtmlParser(title.replace(/ /g, '&nbsp;'))}</div> };
                } else {
                    return { id, action: item.action, include: true, selectable: false, url, element: <div style={{ height: 10 }}></div>, divider: true };
                }
            } else {
                const textColor = item.action === RecipientAction.PotentialDonors ? 'green' : '';
                return { id, action: item.action, include: true, selectable: false, url, element: <a href={url} style={{ color: textColor }}>{ReactHtmlParser(title.replace(/ /g, '&nbsp;'))}</a> };
            }
        });

        return (
            <div style={{ width: '100%', textAlign: 'center' }}>
                <OverlayMenuButton
                    menu={menu}
                    bsStyle='primary'
                    bsSize='xs'
                    title='Actions'
                    listItemStyle={{ padding: '2px 7.5px', fontSize: 14 }}
                    includeCaret={true}
                    width={300}
                    onToggle={handleMenuToggle}
                    onSelect={handleMenuSelection}
                />
            </div>
        );
    }
}

export default ActionButtonRenderer;